<template>
  <div
    v-if="isLoading"
    class="loading"
  >
    <h3>{{ trans('core.loading') }}</h3>
    <div class="progress">
      <div
        class="progress-bar progress-bar-striped"
        role="progressbar"
        :style="{ width: loadingProgress * 100 + '%' }"
      />
    </div>
  </div>

  <subscriptions-matrix-overview
    v-else
    :subscribables="subscribables"
    :student-subscribables="studentSubscribables"
    :is-advanced-rubric="isAdvancedRubric"
    @showFilter="processShowFilter"
  >
    <template slot="filter">
      <event-subscriptions-filter
        v-model="filters"
        :course-id="courseId"
        :subscribable-grouping-id="subscribableGroupingId"
        :api-route="apiRoute"
        :type="selectedType"
        :show-filter="showFilter"
      />
    </template>
    <template
      slot="modal-extra-info"
      slot-scope="props"
    >
      <tr>
        <th>{{ trans('core.event') }}</th>
        <td>{{ nameOfEvent(props.subscribable) }}</td>
      </tr>
      <tr>
        <th>{{ trans('core.date') }}</th>
        <td>{{ dateOfEvent(props.subscribable) }}</td>
      </tr>
    </template>
  </subscriptions-matrix-overview>
</template>

<script>
import { CATEGORY_ADVANCED_RULES, MATRIX_SUBSCRIBABLES_CHUNK_SIZE, SINGLE } from '@/constants'
import EventSubscriptionsFilter from '@/employees/components/Subscribable/EventSubscriptionsFilter'
import SubscriptionsMatrixOverview from '@/employees/components/Subscribable/SubscriptionsMatrixOverview'
import { mapState } from 'vuex'

export default {
  name: 'SingleSubscriptionsMatrixOverview',

  components: { EventSubscriptionsFilter, SubscriptionsMatrixOverview },

  props: {
    apiRoute: {
      required: true,
      type: String,
    },
    courseId: {
      required: false,
      type: String,
      default: null,
    },
    subscribableGroupingId: {
      required: false,
      type: String,
      default: null,
    },
    type: {
      required: false,
      type: String,
      default: 'single',
    },
  },

  data () {
    return {
      filters: { startdate: null, enddate: null, eventNames: [] },
      loadingProgress: 0,
      isLoading: true,
      studentSubscribables: null,
      showFilter: true,
    }
  },

  computed: {
    ...mapState({
      events (state) {
        return state.events.events
      },

      subscribableGrouping (state) {
        return this.subscribableGroupingId ? state.subscribableGroupings.subscribableGroupings[this.subscribableGroupingId] : null
      },

      subscribables (state) {
        let subscribables = state.subscribables.subscribables

        const events = Object.values(this.events)

        subscribables = Object.values(subscribables).filter(subscribable => {
          if (this.filters.startdate != null || this.filters.enddate != null || this.filters.eventNames.length > 0) {
            const eventObject = events.find(event => event.id === subscribable.event)

            if (this.filters.startdate != null && eventObject.starttime < this.filters.startdate) {
              return false
            }

            const enddate = moment(this.filters.enddate)

            if (this.filters.enddate != null && eventObject.starttime > enddate.endOf('day').format('YYYY-MM-DD HH:mm:ss')) {
              return false
            }

            if (this.filters.eventNames.length > 0) {
              const names = this.filters.eventNames.map(eventName => eventName.name)
              if (!names.includes(eventObject.name)) {
                return false
              }
            }
          }

          return true
        })

        subscribables = subscribables.sort(function (a, b) {
          const eventA = events.find(event => event.id === a.event)
          const eventB = events.find(event => event.id === b.event)

          return moment(eventA.starttime) - moment(eventB.starttime)
        })

        return subscribables
      },
    }),

    isAdvancedRubric () {
      if (this.subscribableGrouping) {
        return this.subscribableGrouping.logicCategory === CATEGORY_ADVANCED_RULES
      }

      return false
    },

    selectedType () {
      if (this.type === SINGLE) {
        return 'class'
      } else {
        return 'appointment'
      }
    },
  },
  created () {
    this.loadItems()
  },
  methods: {
    async loadItems () {
      this.isLoading = true
      this.loadingProgress = 0
      let subscribableData = []
      let subscribableIds = []
      const calls = []

      if (this.courseId != null) {
        // get the subscribables for course
        await this.$store.dispatch('getCourseSubscribables', {
          courseId: this.courseId,
        }).then((response) => {
          if (response.subscribables) {
            subscribableIds = response.subscribables.map(subscribable => subscribable.id)
          }
        })
      }

      if (this.subscribableGroupingId != null) {
        // get the subscribable grouping with the subscribables
        await this.$store.dispatch('getSubscribableGroupingSubscribables', {
          subscribableGroupingId: this.subscribableGroupingId,
        }).then((response) => {
          subscribableIds = response.data.data.data.map(subscribable => subscribable.id)
        })
      }

      this.loadingProgress = 0.25

      // split the subscribables of the grouping in chunks for separate calls
      const chunkedSubscribables = _.chunk(subscribableIds, MATRIX_SUBSCRIBABLES_CHUNK_SIZE)
      const singleStepProgression = 0.75 / chunkedSubscribables.length

      // Do a call for each chunk and increment the loading progress
      for (const chunk of chunkedSubscribables) {
        calls.push(this.$store.dispatch('getSubscribables', {
          apiRoute: this.apiRoute,
          queryParams: {
            filter: {
              id: chunk,
            },
          },
        }).then((response) => {
          if (response.student_subscribables) {
            subscribableData = subscribableData.concat(response.student_subscribables)
          }

          this.loadingProgress += singleStepProgression
        }))
      }

      // wait untill all calls are done so we can group the results
      await Promise.all(calls)

      // group the data for the overview
      this.studentSubscribables = _.groupBy(subscribableData, function (studentSubscribable) {
        return studentSubscribable.student
      })

      this.isLoading = false
    },

    dateOfEvent (subscribable) {
      return this.selectedEvent(subscribable) == null ? null : this.selectedEvent(subscribable).displayDate
    },

    nameOfEvent (subscribable) {
      return this.selectedEvent(subscribable) == null ? null : this.selectedEvent(subscribable).name
    },

    selectedEvent (subscribable) {
      return subscribable.event == null ? null : Object.values(this.events).find(event => event.id === subscribable.event)
    },
    processShowFilter (event) {
      this.showFilter = event.showFilter
    },
  },
}
</script>

<style lang="scss" scoped>
.filter-component {
  padding: 0px;

  .control-label {
    padding: 0px;
  }
}
  .filter-component-date {
    .control-label {
      padding-top: 10px;
    }
  }

  .loading {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      flex: 1 0 auto;

      .progress {
          width: 75%;
      }
  }
</style>
